import {
  createSimpleStore,
  SimpleStoreReadOnly
} from '@passionware/simple-store';

/**
 * Asks whether to drop the unsaved draft named `title`; `true` discards it.
 * When the user keeps it, the prompt hands focus back through `focus` once it
 * has closed.
 */
export type ConfirmDiscard = (draft: {
  title: string;
  focus(): void;
}) => Promise<boolean>;

/** An editor in edit mode, as the session sees it. */
export type Editor = {
  /** Names the draft in the discard prompt. */
  title: string;
  /** Leaves edit mode without saving. */
  discard(): void;
  /** Moves focus back into the editor (the user kept its draft). */
  focus(): void;
  /** Whether the editor is rendered inside `node`. */
  isWithin(node: Node): boolean;
};

/** An editor's link to the session for as long as it stays in edit mode. */
export type EditorPresence = {
  /** Whether the draft differs from the saved values. */
  setDirty(dirty: boolean): void;
  /** While saving, the editor keeps edit mode: requests to let go are ignored. */
  setSaving(saving: boolean): void;
  /** Leaves edit mode without saving, asking first when the draft is dirty. */
  requestLeave(): void;
  /** Edit mode ended on the editor's side (saved, cancelled, unmounted). */
  leave(): void;
};

export type ExclusiveEdit = {
  /**
   * Opens an editor: `open` runs once the editor in edit mode has let go, at
   * once when its draft is clean and after the user agreed to discard it
   * otherwise. Nothing runs when the user keeps editing.
   */
  requestEdit(open: () => void): void;
  /**
   * Before `node` is hidden (a section collapses): an editor in edit mode
   * inside it lets go the same way, then `hide` runs. Editors elsewhere keep
   * editing.
   */
  releaseWithin(node: Node, hide: () => void): void;
  /** Called by an editor entering edit mode. */
  enter(editor: Editor): EditorPresence;
  /** Whether an editor in edit mode holds unsaved changes. */
  hasUnsavedDraft: SimpleStoreReadOnly<boolean>;
};

type Entry = { editor: Editor; dirty: boolean; saving: boolean };

const noop = () => {};

/**
 * Editors take turns: every request to open one asks the editors in edit mode
 * to let go first, so at most one is editing and a dirty draft is never
 * dropped without the user's consent (`confirmDiscard`).
 */
export function createExclusiveEdit(
  confirmDiscard: ConfirmDiscard
): ExclusiveEdit {
  const editing = new Set<Entry>();
  const hasUnsavedDraft = createSimpleStore(false);
  let prompting = false;

  function sync() {
    const dirty = [...editing].some(entry => entry.dirty);
    if (dirty !== hasUnsavedDraft.getCurrentValue()) {
      hasUnsavedDraft.setNewValue(dirty);
    }
  }

  function discard(entry: Entry) {
    editing.delete(entry);
    sync();
    entry.editor.discard();
  }

  /** `entries` let go, asking the user first when one of them is dirty; then `then` runs. */
  function handOver(entries: Entry[], then: () => void) {
    // A save in flight decides itself whether the draft stays.
    if (entries.some(entry => entry.saving)) return;
    const dirty = entries.find(entry => entry.dirty);
    if (!dirty) {
      entries.forEach(discard);
      then();
      return;
    }
    // The prompt is modal: another request cannot come from the user meanwhile.
    if (prompting) return;
    prompting = true;
    void confirmDiscard({
      title: dirty.editor.title,
      focus: () => dirty.editor.focus()
    })
      .finally(() => {
        prompting = false;
      })
      .then(confirmed => {
        if (!confirmed) return;
        // Entries that left edit mode while the prompt was open are gone already.
        entries.filter(entry => editing.has(entry)).forEach(discard);
        then();
      });
  }

  return {
    requestEdit(open) {
      handOver([...editing], open);
    },
    releaseWithin(node, hide) {
      handOver(
        [...editing].filter(entry => entry.editor.isWithin(node)),
        hide
      );
    },
    enter(editor) {
      const entry: Entry = { editor, dirty: false, saving: false };
      editing.add(entry);
      return {
        setDirty(dirty) {
          entry.dirty = dirty;
          if (editing.has(entry)) sync();
        },
        setSaving(saving) {
          entry.saving = saving;
        },
        requestLeave() {
          if (editing.has(entry)) handOver([entry], noop);
        },
        leave() {
          editing.delete(entry);
          sync();
        }
      };
    },
    hasUnsavedDraft
  };
}
